import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogContent,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ODDS_HISTORY_API_URL } from '../config';

// ══════════════════════════════════════════════════════════
//  HELPERS
// ══════════════════════════════════════════════════════════

const fmtPrice = (p) => (p == null ? '-' : p >= 0 ? `+${p}` : `${p}`);

const fmtTime = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  const month = String(d.getMonth() + 1).padStart(2, '\u00A0');
  const day = String(d.getDate()).padStart(2, '\u00A0');
  const time = d.toLocaleString('en-US', {
    hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true,
  });
  return `${month}/${day} ${time}`;
};

const fmtEventStart = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleString('en-US', {
    month: 'numeric', day: 'numeric', year: '2-digit',
    hour: 'numeric', minute: '2-digit', hour12: true,
  }).replace(',', '');
};

// ══════════════════════════════════════════════════════════
//  HOOK
// ══════════════════════════════════════════════════════════

export function useOddsHistory() {
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyMeta, setHistoryMeta] = useState(null);

  const openOddsHistory = useCallback(async (meta, bookName) => {
    if (!meta) return;
    setHistoryMeta({ ...meta, bookName });
    setHistoryOpen(true);
    setHistoryLoading(true);
    setHistoryData([]);
    try {
      const params = new URLSearchParams({
        fixture_id: meta.fixture_id || '',
        sportsbook: bookName || '',
        market_id: meta.market_id || '',
        selection: meta.selection || '',
        selection_line: meta.selection_line || '',
        player_id: meta.player_id || '',
      });
      const resp = await fetch(`${ODDS_HISTORY_API_URL}?${params}`);
      if (resp.ok) {
        const data = await resp.json();
        setHistoryData(data.history || []);
      }
    } catch (err) {
      console.error('[ODDS-HISTORY] Fetch error:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const closeOddsHistory = useCallback(() => setHistoryOpen(false), []);

  return { historyOpen, historyData, historyLoading, historyMeta, openOddsHistory, closeOddsHistory };
}

// ══════════════════════════════════════════════════════════
//  MODAL COMPONENT
// ══════════════════════════════════════════════════════════

export default function OddsHistoryModal({ open, onClose, data, loading, meta }) {
  if (!open) return null;

  // Display fields
  const selectionDisplay = meta?.playerName || meta?.selection || '';
  const marketDisplay = meta?.marketDisplay || '';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#1a1a1a',
          color: '#ffffff',
          maxHeight: '85vh',
          maxWidth: '340px',
          borderRadius: '8px',
          border: '1px solid #333',
        }
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* ── Header ── */}
        <Box sx={{ px: 2.5, pt: 2, pb: 1.5, borderBottom: '1px solid #2a2a2a' }}>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ position: 'absolute', top: 8, right: 8, color: '#555', '&:hover': { color: '#fff' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          {/* Line 1: Selection · Book logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.3, pr: 4 }}>
            <Typography sx={{ fontSize: '0.85rem', color: '#aaa' }}>
              {selectionDisplay}
            </Typography>
            {meta?.bookLogo && (
              <img src={meta.bookLogo} alt="" style={{ width: 22, height: 22, objectFit: 'contain', borderRadius: 3 }} />
            )}
          </Box>

          {/* Line 2: Market */}
          {marketDisplay && (
            <Typography sx={{ fontSize: '0.75rem', color: '#888', mb: 0.3 }}>
              {marketDisplay}
            </Typography>
          )}

          {/* Line 3: League | away logo ABR @ home logo ABR */}
          {(meta?.league || meta?.awayTeam) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
              {meta.league && (
                <>
                  <Typography sx={{ fontSize: '0.75rem', color: '#888' }}>{meta.league}</Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: '#444', mx: 0.3 }}>|</Typography>
                </>
              )}
              {meta.awayTeamLogo && (
                <img src={meta.awayTeamLogo} alt="" style={{ width: 14, height: 14, objectFit: 'contain' }} />
              )}
              {meta.awayTeam && (
                <Typography sx={{ fontSize: '0.75rem', color: '#aaa' }}>{meta.awayTeam}</Typography>
              )}
              <Typography sx={{ fontSize: '0.7rem', color: '#555' }}>@</Typography>
              {meta.homeTeamLogo && (
                <img src={meta.homeTeamLogo} alt="" style={{ width: 14, height: 14, objectFit: 'contain' }} />
              )}
              {meta.homeTeam && (
                <Typography sx={{ fontSize: '0.75rem', color: '#aaa' }}>{meta.homeTeam}</Typography>
              )}
            </Box>
          )}

          {/* Line 4: Event start */}
          {meta?.eventStart && (
            <Typography sx={{ fontSize: '0.72rem', color: '#666' }}>{fmtEventStart(meta.eventStart)}</Typography>
          )}
        </Box>

        {/* ── Content ── */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={24} sx={{ color: '#555' }} />
          </Box>
        ) : data.length === 0 ? (
          <Box sx={{ py: 5, textAlign: 'center' }}>
            <Typography sx={{ color: '#555', fontSize: '0.85rem' }}>No history available</Typography>
          </Box>
        ) : (
          <>
            {/* Table */}
            <Box sx={{ px: 1.5, pb: 1.5, pt: 1, overflowY: 'auto', maxHeight: '60vh' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={TH_STYLE}>Time</th>
                    <th style={{ ...TH_STYLE, textAlign: 'left', width: '1%', whiteSpace: 'nowrap' }}>Odds</th>
                  </tr>
                </thead>
                <tbody>
                  {[...data].reverse().map((h, i) => {
                    const isLocked = h.event_type === 'locked';
                    const pts = h.points != null
                      ? (h.points % 1 === 0 ? `${h.points}\u00A0\u00A0` : `${h.points}`)
                      : '';
                    const price = fmtPrice(h.price);
                    const oddsText = pts !== '' ? `${pts} ${price}` : price;
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid #222' }}>
                        <td style={{ ...TD_STYLE, color: '#999', whiteSpace: 'nowrap' }}>
                          {fmtTime(h.captured_at)}
                        </td>
                        <td style={{
                          ...TD_STYLE,
                          textAlign: 'right',
                          color: '#ccc',
                          fontWeight: 500,
                          fontVariantNumeric: 'tabular-nums',
                          fontFamily: 'monospace',
                          whiteSpace: 'nowrap',
                          textDecoration: isLocked ? 'line-through' : 'none',
                        }}>
                          {oddsText}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

const TH_STYLE = {
  textAlign: 'left',
  padding: '6px 8px',
  color: '#555',
  fontSize: '0.8rem',
  fontWeight: 500,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  borderBottom: '1px solid #2a2a2a',
};

const TD_STYLE = {
  padding: '6px 8px',
  fontSize: '0.85rem',
};